const getWindowConfig = () => {
  if (typeof window === 'undefined' || !window.__APP_CONFIG__) {
    return {};
  }

  return window.__APP_CONFIG__;
};

const getEnvConfig = () => {
  return {
    REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL || '',
    REACT_APP_API_URL: process.env.REACT_APP_API_URL || '',
    REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL || '',
    REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID || '',
    REACT_APP_APPINSIGHTS_CONNECTION_STRING: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '',
    REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY: process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY || '',
    REACT_APP_NODE_ENV: process.env.REACT_APP_NODE_ENV || '',
    NODE_ENV: process.env.NODE_ENV || ''
  };
};

const maskConfigValue = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  const trimmedValue = value.trim();
  if (trimmedValue.length <= 12) {
    return `${trimmedValue.slice(0, 4)}...`;
  }

  return `${trimmedValue.slice(0, 6)}...${trimmedValue.slice(-6)}`;
};

const logGoogleConfigDiagnostics = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const runtimeConfig = getWindowConfig();
  const envConfig = getEnvConfig();

  console.log('[runtimeConfig] Google client ID diagnostics', {
    runtimeKeys: Object.keys(runtimeConfig),
    runtimeHasReactAppGoogleClientId: !!runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID,
    runtimeHasGoogleClientId: !!runtimeConfig.GOOGLE_CLIENT_ID,
    envHasReactAppGoogleClientId: !!envConfig.REACT_APP_GOOGLE_CLIENT_ID,
    envHasGoogleClientId: !!envConfig.GOOGLE_CLIENT_ID,
    runtimeReactAppGoogleClientIdSample: maskConfigValue(runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID),
    runtimeGoogleClientIdSample: maskConfigValue(runtimeConfig.GOOGLE_CLIENT_ID),
    envReactAppGoogleClientIdSample: maskConfigValue(envConfig.REACT_APP_GOOGLE_CLIENT_ID),
    envGoogleClientIdSample: maskConfigValue(envConfig.GOOGLE_CLIENT_ID),
    nodeEnv: envConfig.NODE_ENV,
    reactAppNodeEnv: envConfig.REACT_APP_NODE_ENV
  });
};

const getConfigValueWithSource = (...keys) => {
  const runtimeConfig = getWindowConfig();
  for (const key of keys) {
    const runtimeValue = runtimeConfig[key];
    if (typeof runtimeValue === 'string' && runtimeValue.trim()) {
      return {
        key,
        value: runtimeValue.trim(),
        source: 'window.__APP_CONFIG__'
      };
    }
  }

  const envConfig = getEnvConfig();
  for (const key of keys) {
    const envValue = envConfig[key];
    if (typeof envValue === 'string' && envValue.trim()) {
      return {
        key,
        value: envValue.trim(),
        source: 'process.env'
      };
    }
  }

  return {
    key: keys[0] || '',
    value: '',
    source: 'missing'
  };
};

const getConfigValue = (...keys) => {
  const runtimeConfig = getWindowConfig();
  for (const key of keys) {
    const runtimeValue = runtimeConfig[key];
    if (typeof runtimeValue === 'string' && runtimeValue.trim()) {
      return runtimeValue.trim();
    }
  }

  const envConfig = getEnvConfig();
  for (const key of keys) {
    const envValue = envConfig[key];
    if (typeof envValue === 'string' && envValue.trim()) {
      return envValue.trim();
    }
  }

  return '';
};

const stripTrailingSlash = (value) => value.replace(/\/+$/, '');

const getFallbackServerUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://${hostname}:3001`;
    }

    if (origin.includes('azurecontainerapps.io')) {
      return origin.replace('app-frontend-', 'app-backend-');
    }

    return origin;
  }

  return 'http://localhost:3001';
};

export const getRuntimeConfig = () => ({
  REACT_APP_SERVER_URL: getConfigValue('REACT_APP_SERVER_URL'),
  REACT_APP_API_URL: getConfigValue('REACT_APP_API_URL'),
  REACT_APP_BACKEND_URL: getConfigValue('REACT_APP_BACKEND_URL'),
  REACT_APP_GOOGLE_CLIENT_ID: getConfigValue('REACT_APP_GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_ID'),
  REACT_APP_APPINSIGHTS_CONNECTION_STRING: getConfigValue('REACT_APP_APPINSIGHTS_CONNECTION_STRING'),
  REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY: getConfigValue('REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY'),
  REACT_APP_NODE_ENV: getConfigValue('REACT_APP_NODE_ENV')
});

export const getServerUrl = () => {
  const configuredUrl = getConfigValue(
    'REACT_APP_SERVER_URL',
    'REACT_APP_BACKEND_URL',
    'REACT_APP_API_URL'
  );

  if (configuredUrl) {
    return stripTrailingSlash(configuredUrl);
  }

  return stripTrailingSlash(getFallbackServerUrl());
};

export const getApiBaseUrl = () => `${getServerUrl()}/api`;

export const getGooglePhotosApiUrl = () => `${getApiBaseUrl()}/google-photos`;

export const getGoogleClientId = () => getConfigValue('REACT_APP_GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_ID');

export const getGoogleClientIdDiagnostics = () => {
  const resolved = getConfigValueWithSource('REACT_APP_GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_ID');
  const runtimeConfig = getWindowConfig();
  const envConfig = getEnvConfig();

  return {
    key: resolved.key || 'REACT_APP_GOOGLE_CLIENT_ID',
    resolvedValue: resolved.value,
    resolvedValueSample: maskConfigValue(resolved.value),
    source: resolved.source,
    hasRuntimeValue:
      (typeof runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID === 'string' && !!runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID.trim()) ||
      (typeof runtimeConfig.GOOGLE_CLIENT_ID === 'string' && !!runtimeConfig.GOOGLE_CLIENT_ID.trim()),
    hasEnvValue:
      (typeof envConfig.REACT_APP_GOOGLE_CLIENT_ID === 'string' && !!envConfig.REACT_APP_GOOGLE_CLIENT_ID.trim()) ||
      (typeof envConfig.GOOGLE_CLIENT_ID === 'string' && !!envConfig.GOOGLE_CLIENT_ID.trim()),
    runtimeReactAppGoogleClientIdSample: maskConfigValue(runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID),
    runtimeGoogleClientIdSample: maskConfigValue(runtimeConfig.GOOGLE_CLIENT_ID),
    envReactAppGoogleClientIdSample: maskConfigValue(envConfig.REACT_APP_GOOGLE_CLIENT_ID),
    envGoogleClientIdSample: maskConfigValue(envConfig.GOOGLE_CLIENT_ID)
  };
};

logGoogleConfigDiagnostics();

export const getAppInsightsConnectionString = () =>
  getConfigValue('REACT_APP_APPINSIGHTS_CONNECTION_STRING');

export const getAppInsightsInstrumentationKey = () =>
  getConfigValue('REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY');

export const getConfiguredNodeEnv = () =>
  getConfigValue('REACT_APP_NODE_ENV') || getEnvConfig().NODE_ENV || '';